import { Habit, ScheduleEntryState, Task, Timestamp } from '@griply/types';
import {
  getActiveSchedule,
  getOverdueDate,
  getScheduleByDate,
  getScheduleDayTarget,
  isTaskCompletedOnDate,
} from '@griply/utils';
import { isSameDay } from 'date-fns';
import {
  useTaskTodayControls,
  useUpdateHabitScheduleEntry,
} from 'features/tasks';
import { useMemo } from 'react';
import { useOpenManualEntryLogging } from 'shared/contexts/habit-manual-entry-logging';
import { useToday } from 'shared/contexts/today';

import { OverdueEntryPrompt } from './types';

export type UseCompleteListActionsOptions = {
  date?: Timestamp;
  onOverdue: (prompt: OverdueEntryPrompt) => void;
  onClose: () => void;
};

export const useCompleteListActions = (
  task: Task,
  { date, onOverdue, onClose }: UseCompleteListActionsOptions,
) => {
  const today = useToday();
  const { onComplete: completeEntry } = useTaskTodayControls();
  const updateHabitEntry = useUpdateHabitScheduleEntry();
  const openManualEntryLogging = useOpenManualEntryLogging();

  const completed = useMemo(
    () => isTaskCompletedOnDate(task, { date, today }),
    [date, task, today],
  );

  const overdueDate = useMemo(
    () => getOverdueDate({ task, dueDate: date, today }),
    [date, task, today],
  );

  const completeOnDate = (entryDate: Timestamp) => {
    const dateSchedule = getScheduleByDate(task.schedules, {
      date: entryDate,
      today,
    });
    const dayTarget = dateSchedule ? getScheduleDayTarget(dateSchedule) : 1;
    completeEntry(task, ScheduleEntryState.Complete, {
      referenceDate: entryDate,
      value: completed ? 0 : dayTarget,
    });
  };

  const skipOnDate = (entryDate: Timestamp) =>
    updateHabitEntry(task as Habit, entryDate, ScheduleEntryState.Skip);

  const failOnDate = (entryDate: Timestamp) =>
    updateHabitEntry(task as Habit, entryDate, ScheduleEntryState.Failure);

  const logOnDate = (entryDate: Timestamp) =>
    openManualEntryLogging(task.id, entryDate);

  const runEntryAction = (
    type: ScheduleEntryState,
    onDate: (entryDate: Timestamp) => void,
    canPrompt = true,
  ) => {
    if (overdueDate && canPrompt) {
      onOverdue({ type, overdueDate, onDate });
      return;
    }

    onDate(date ?? today);
    onClose();
  };

  const onComplete = () =>
    runEntryAction(ScheduleEntryState.Complete, completeOnDate, !completed);

  const onSkip = () => runEntryAction(ScheduleEntryState.Skip, skipOnDate);

  const onFail = () => runEntryAction(ScheduleEntryState.Failure, failOnDate);

  const onLog = () => runEntryAction(ScheduleEntryState.Complete, logOnDate);

  const skippedTodayOrAfter = useMemo(
    () =>
      getActiveSchedule(task.schedules, today)?.entries.find(
        (entry) =>
          entry.state === ScheduleEntryState.Skip &&
          isSameDay(entry.date, today),
      ),
    [task.schedules, today],
  );
  const failedToday = useMemo(
    () =>
      getActiveSchedule(task.schedules, today)?.entries.find(
        (entry) =>
          entry.state === ScheduleEntryState.Failure &&
          isSameDay(entry.date, today),
      ),
    [task.schedules, today],
  );

  return {
    completed: !!completed,
    skippedTodayOrAfter: !!skippedTodayOrAfter,
    failedToday: !!failedToday,
    onComplete,
    onSkip,
    onFail,
    onLog,
  };
};
